import type { ReactNode } from "react";
import { useEffect, useRef } from "react";

export interface LogConsoleProps {
  /** Optional row above the console — a title, an export button. */
  header?: ReactNode;
  /**
   * Whatever changes when a new line arrives. The console scrolls to the bottom
   * on every change to this value, so pass the log array or its length rather
   * than a value that is stable across appends.
   */
  scrollKey: unknown;
  children: ReactNode;
  className?: string;
}

/**
 * The scrolling console chrome shared by the training and conversion logs.
 *
 * Presentational only — it owns the dark card, the thin scrollbar and the
 * stick-to-bottom behaviour, and knows nothing about what a log line is. The
 * two callers render very different rows (structured per-epoch metrics versus
 * timestamped text) but must scroll identically.
 *
 * Sticking to the bottom yields as soon as the user scrolls up, and resumes
 * when they scroll back down: reading a line partway up a log that is still
 * being appended to should not yank the view away.
 */
export const LogConsole = ({
  header,
  scrollKey,
  children,
  className,
}: LogConsoleProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const isUserScrolledUp = useRef(false);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const threshold = 30;
    isUserScrolledUp.current =
      el.scrollHeight - el.scrollTop - el.clientHeight > threshold;
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (el && !isUserScrolledUp.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [scrollKey]);

  return (
    <div
      className={
        className ?? "mb-6 flex min-h-100 flex-1 flex-col overflow-hidden pb-4"
      }
    >
      {header && (
        <div className="flex shrink-0 flex-row items-center justify-between py-2">
          {header}
        </div>
      )}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl bg-[#0f0f18] px-4">
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto pr-2 [scrollbar-color:rgba(255,255,255,0.15)_transparent] [scrollbar-width:thin] [&::-webkit-scrollbar-thumb:hover]:bg-white/25 [&::-webkit-scrollbar-thumb]:rounded-[3px] [&::-webkit-scrollbar-thumb]:bg-white/15 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:w-1.5"
        >
          {children}
        </div>
      </div>
    </div>
  );
};
